// FSA Template API Types — fsa.tbl_fs_template + hierarchy
// Per prima-web/CLAUDE.md Module Boundaries: FSA types live under api/types/fsa/.
//
// As of fix 019 (schema refactor) the reusable tbl_format_definition
// registry was dropped. Each schedule now owns its own format_type +
// column_spec + additional_disclosures inline. Cross-note auto-reflection
// is modelled via class.classRole + class.targetClassId.

import type { ID } from '../common.types';

// ============================================================================
// FORMAT SHAPE (now per-schedule)
// ============================================================================

export type FormatType =
  | 'MOVEMENT'
  | 'LISTING'
  | 'SUB_CLASSIFICATION'
  | 'AGEING'
  | 'RECONCILIATION'
  | 'DISCLOSURE'
  | 'NARRATIVE'
  | 'CUSTOM';

export type ColumnDataType = 'amount' | 'integer' | 'text' | 'enum' | 'percent' | 'date';

/**
 * Single column descriptor inside a schedule's column_spec JSONB array.
 * The shape is the same as the previous reusable format-definition column
 * spec — only the location moved (now lives on the schedule itself).
 *
 * Two-level headers: when consecutive columns share the same `group`
 * value, they render under a merged top-row cell with that group label.
 * Used for Schedule III "As at <date>" period grouping in Note 1
 * (Share Capital), where No. of Shares + Amount sit under one date for
 * CY and another for PY.
 */
export interface FormatColumnSpec {
  code: string;
  label: string;
  /**
   * Optional override of <c>label</c> for the printed PDF. Lets a
   * column read as a compact abbreviation on screen ("CY", "PY") and
   * a verbose phrase on print ("Current Year", "Previous Year").
   * Renderer reads <c>print_label ?? label</c>; the in-modal UI
   * always uses <c>label</c>. Optional everywhere — most columns omit
   * it and print the same text they show on screen.
   */
  print_label?: string;
  data_type: ColumnDataType;
  /** Group label for two-level header rendering. Consecutive columns with the same value merge under it. */
  group?: string;
  is_computed?: boolean;
  /** Arithmetic expression referring to other column codes; e.g. "opening_gross + additions - deletions". */
  formula?: string;
  /**
   * FSA-2.2 — declarative deduction flag. When TRUE, the column's value
   * is **negated** when it appears as an operand in any other column's
   * formula. Lets formulas use `+` everywhere while still subtracting
   * deductions semantically — eliminating the "did the author remember
   * the `-`?" failure mode. Auditor still types positive amounts into
   * the cell; the negation happens at evaluation time.
   *
   * Used today on PPE / Intangible Assets `gross_deletions` and
   * `dep_on_deletions`. Honoured by `evaluateFormula` +
   * `recomputeFormulaColumns` + `MultiColumnClassTable.recomputeRow`.
   */
  is_deduction?: boolean;
  is_optional?: boolean;
  /** For data_type = 'enum'. */
  options?: string[];
  align?: 'left' | 'right' | 'center';
  /**
   * When true: the field is captured per row at FSA-3 entry time (and shows
   * in the "Data Captured Per Row" strip), but is NOT rendered as a column
   * in the Note Breakdown print table. Used for fields that drive grouping
   * or metadata but should not appear in the printed FS — e.g., Share Type
   * on Note 1, where rows are visually grouped under "A. Equity Share
   * Capital" / "B. Preference Share Capital" sub-headings instead of having
   * a Share Type column.
   */
  is_grouping_field?: boolean;

  /**
   * FSA-2.2 multi-column class table (Phase 1 — Option A foundation).
   * Marks the column whose row values roll up to the Note's closing
   * total — i.e. the column the BS NOTE_TOTAL references (Net Block for
   * PPE / Intangibles). Drift validation compares Σ this column against
   * Σ TB classified amounts. Exactly one column per multi-column spec
   * carries this flag; common 2-col specs leave it unset (the resolver
   * falls back to legacy amount_actual).
   */
  is_total?: boolean;

  /**
   * FSA-2.2 multi-column class table — marks the column displayed as
   * the prior-period comparative (drives opening_actual_total and the
   * BS Prior Year cell). Optional even on multi-column specs; PPE
   * declares it on `net_py`.
   */
  is_prior_year?: boolean;

  /**
   * FSA-2.2 multi-column class table — Phase 2.6.
   * Reconciliation formula. The cell's saved value is auditor-entered
   * (or TB-sourced), NOT auto-computed; the formula here defines an
   * EXPECTED value that the saved value should match. The UI surfaces
   * a drift chip when |saved − evaluate(drift_formula)| > tolerance.
   *
   * Example on PPE's `net_closing` column:
   *   "drift_formula": "gross_closing - dep_closing"
   * → if the auditor enters Net Block from the TB but the gross / dep
   *   movement they typed doesn't add up to it, the chip flags it.
   *
   * NULL when no reconciliation rule applies. The column stays plain
   * editable amount in that case.
   */
  drift_formula?: string;

  /**
   * FSA — ICAI NCE: when TRUE the column is read-only in the grid (the
   * value is DERIVED, not auditor-entered). Pairs with {@link derived_from}.
   * e.g. Owners' Capital `remuneration` is consumed from the Remuneration
   * note, so the auditor edits it there, not in the capital note.
   */
  read_only?: boolean;

  /**
   * FSA — ICAI NCE: declares this column's per-partner value is CONSUMED
   * from another note. The backend reads the source note's per-partner line
   * items and ships them on `hydrate.partnerDerivedColumns`; the merge writes
   * them (read-only) into this column on the `target_class_code` section.
   */
  derived_from?: {
    schedule_code: string;
    class_code: string;
    /** "partner" — matched by line-item local_id "partner::<id>". */
    match?: string;
    /** Source line-item field to read (default "amount_actual"). */
    value?: string;
    /** Which section of THIS note the appropriation lands in (e.g. PC_CURRENT_ACCOUNT). */
    target_class_code?: string;
  };
}

/**
 * Note-level disclosure — separate from class-level disclosure_rules.
 * Lives in tbl_template_schedule.additional_disclosures JSONB array.
 *
 * Three disclosure shapes are supported:
 *   • CHOICE  — `possible_values` set; user picks one (or each row has one)
 *   • TEXT    — `free_text: true`; user enters paragraph(s)
 *   • GRID    — `column_spec` set; user fills a table (rows added freely),
 *               e.g., Shareholders Holding More than 5%, Promoter Holding
 *   • RULE    — none of the above; presentation rule (no input required)
 */
export interface AdditionalDisclosure {
  code: string;
  label: string;
  description: string;
  /** CHOICE shape — the disclosure value is one of these. */
  possible_values?: string[];
  /** TEXT shape — captured as free text per occurrence. */
  free_text?: boolean;
  /**
   * GRID shape — disclosure is a tabular grid (e.g., Shareholders > 5%).
   * Each entry describes one column. Users add rows freely at FSA-3 entry.
   */
  column_spec?: FormatColumnSpec[];
}

// ============================================================================
// TEMPLATE HEADER (fsa.tbl_fs_template)
// ============================================================================

export type EntityType = 'COMPANY' | 'LLP' | 'PARTNERSHIP' | 'SOLE_PROPRIETOR' | 'TRUST';

/** Browser-row discriminator — ADR-0005 unified Template Browser listing. */
export type BrowserRowKind = 'TEMPLATE' | 'NARRATIVE';

export interface FsTemplate {
  id: string;
  /** TEMPLATE (fsa.tbl_fs_template) or NARRATIVE (fsa.tbl_narrative_component; only when includeNarratives). Narrative rows carry code/name/version/status only. */
  kind: BrowserRowKind;
  /** Template version (ADR-0004) or narrative component version (ADR-0005). */
  version: number;
  code: string;
  name: string;
  description?: string | null;

  entityType: EntityType;
  jurisdictionCode: string;     // ISO 3166-1 alpha-2
  statutoryBasis: string;       // e.g. SCHEDULE_III_DIV_I

  applicableFrom: string;
  applicableTo?: string | null;

  isSystem: boolean;
  baseTemplateId?: string | null;

  defaultCurrencyId?: string | null;
  defaultCurrencyCode?: string | null;
  defaultCurrencyName?: string | null;
  defaultCurrencySymbol?: string | null;

  defaultRoundingFactorEnumId?: string | null;
  defaultRoundingFactorKey?: string | null;
  defaultRoundingFactorLabel?: string | null;

  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;

  rowVersion: number;
  createdAt: string;
  createdBy?: ID | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  updatedByName?: string | null;
}

// ============================================================================
// HIERARCHY (assembled by GET /FsaTemplates/{id}/full)
// ============================================================================

export type ReportType = 'FINANCIAL_STATEMENT' | 'NOTES_BUNDLE' | 'NARRATIVE';

export interface TemplateReport {
  id: string;
  templateId: string;
  code: string;
  name: string;
  description?: string | null;
  reportType: ReportType;
  isOptional: boolean;
  displayOrder: number;
  /**
   * Layout JSON for FS reports (BS / PL / CF). NULL for NOTES_BUNDLE and
   * NARRATIVE reports. Helper `parseLayout` returns the typed
   * `ReportLayout` shape.
   */
  layout?: string | null;
}

// --------------------------------------------------------------------------
// FS layout — JSON document on the report for BS / PL / CF.
// Each line carries its own kind + code. The compositor (FSA-2.2) evaluates
// top-down at render time:
//   NOTE_TOTAL  → sum of classified ledgers under the named note (filtered by class if present)
//   VALUE       → engagement-time manual entry keyed by line.code
//   SUBTOTAL/TOTAL → formula evaluated against earlier line codes
// --------------------------------------------------------------------------

export type LayoutLineKind =
  | 'SECTION_HEADER'
  | 'NOTE_TOTAL'
  | 'VALUE'
  | 'SUBTOTAL'
  | 'TOTAL';

export interface LayoutLine {
  kind: LayoutLineKind;
  code?: string;
  label?: string;
  /** 0..3 — visual indent in steps. Defaults to 0 when absent. */
  indent?: number;
  /** For NOTE_TOTAL — references TemplateSchedule.code under the same template. */
  note_schedule_code?: string;
  /** For NOTE_TOTAL — optional filter pulling a single class's amount instead of the note total. */
  note_class_code?: string;
  /** For SUBTOTAL / TOTAL — formula with `+`/`-` operators and earlier line codes. */
  formula?: string;
  /** Render hint for the row. Defaults to true for SUBTOTAL/TOTAL. */
  is_bold?: boolean;
}

export interface ReportLayout {
  lines: LayoutLine[];
}

export function parseLayout(r: TemplateReport | null | undefined): ReportLayout | null {
  if (!r || !r.layout) return null;
  try { return JSON.parse(r.layout); } catch { return null; }
}

export type ScheduleType = 'LINE_GROUP' | 'NOTE';

export interface TemplateSchedule {
  id: string;
  reportId: string;
  code: string;
  name: string;
  description?: string | null;
  scheduleType: ScheduleType;
  /**
   * Order of appearance within the parent report. Note numbers on the
   * printed BS/P&L are NOT a template attribute — they are renumbered per
   * engagement based on which notes contain non-zero data. The template
   * only declares ordering and cross-links by name/code.
   */
  displayOrder: number;

  /** Rendering hint; NULL = CUSTOM. */
  formatType?: FormatType | null;
  /** JSON string from the API; consumer must JSON.parse. Helper `parseColumnSpec` provided. */
  columnSpec: string;
  /** JSON string from the API; consumer must JSON.parse. Helper `parseAdditionalDisclosures` provided. */
  additionalDisclosures: string;
  /**
   * JSON string — optional boilerplate text blocks rendered around the
   * Note's data table. Helper `parseNarrativeBlocks` provided.
   * Empty array when null.
   */
  narrativeBlocks?: string | null;
}

export function parseColumnSpec(s: TemplateSchedule): FormatColumnSpec[] {
  try { return JSON.parse(s.columnSpec || '[]'); } catch { return []; }
}
export function parseAdditionalDisclosures(s: TemplateSchedule): AdditionalDisclosure[] {
  try { return JSON.parse(s.additionalDisclosures || '[]'); } catch { return []; }
}

export interface ScheduleNarrativeBlock {
  position: 'BEFORE' | 'AFTER';
  body_html: string;
  is_optional?: boolean;
}

export function parseNarrativeBlocks(s: TemplateSchedule): ScheduleNarrativeBlock[] {
  try { return JSON.parse(s.narrativeBlocks || '[]'); } catch { return []; }
}

export type ClassRole =
  | 'PRIMARY'
  | 'SHORT_TERM_PORTION'
  | 'CONTRA'
  | 'RECLASSIFICATION'
  | 'MANUAL_DISCLOSURE'
  | 'MEMO_ONLY';

/**
 * One row in a schedule's data-entry form, OR a sub-classification grouping
 * when it has children. Recursive via parent_class_id (nested under
 * `children`). Carries computed-class metadata, class-level disclosure
 * flags, and optional cross-note target reference.
 */
export interface ScheduleClass {
  id: string;
  /** Direct FK to the owning schedule (replaces the old section_id chain). */
  scheduleId: string;
  /**
   * Optional layout-only section heading (e.g. "Authorised Capital",
   * "Issued and Paid up" on Note 1). NULL when the class doesn't belong
   * to a named section. Free-text; doesn't affect classification.
   */
  sectionLabel?: string | null;
  parentClassId?: string | null;

  code: string;
  name: string;
  description?: string | null;

  linkedNoteScheduleId?: string | null;
  linkedNoteScheduleCode?: string | null;
  /** The linked note's display name (e.g. "Long-Term Borrowings"). Used in template view since note numbers are engagement-time. */
  linkedNoteScheduleName?: string | null;

  /** JSON string. Use `parseDisclosureRules` to get the typed object. */
  disclosureRules: string;

  isComputed: boolean;
  /** JSON string. Use `parseComputationRule` to get the typed object. */
  computationRule: string;

  /** Cross-note role tag; NULL is treated as PRIMARY. */
  classRole?: ClassRole | null;
  /** When set, this class's amount auto-reflects in the referenced class in another note. */
  targetClassId?: string | null;
  targetClassCode?: string | null;
  targetClassName?: string | null;
  targetClassScheduleCode?: string | null;
  targetClassScheduleName?: string | null;

  displayOrder: number;
  children: ScheduleClass[];
}

export interface DisclosureRules {
  show_secured_unsecured_split?: boolean;
  show_director_guaranteed_aggregate?: boolean;
  order_bonds_by_maturity_desc?: boolean;
  show_continuing_default?: boolean;
  show_repayment_terms?: boolean;
  redeemable_bonds_reissue_disclosure?: boolean;
  show_msme_disclosure?: boolean;
  show_ageing_buckets?: boolean;
  show_promoter_holding?: boolean;
  show_shareholders_above_5pct?: boolean;
  [key: string]: unknown;
}

export type ComputationRule =
  | { type: 'SUM'; operands: string[] }
  | { type: 'SUBTRACT'; minuend: string; subtrahends: string[] }
  | { type: 'FORMULA'; expression: string }
  | { type: string; [key: string]: unknown }; // forward-compat

export function parseDisclosureRules(c: ScheduleClass): DisclosureRules {
  try { return JSON.parse(c.disclosureRules || '{}'); } catch { return {}; }
}
export function parseComputationRule(c: ScheduleClass): ComputationRule | null {
  if (!c.isComputed) return null;
  try { return JSON.parse(c.computationRule || '{}'); } catch { return null; }
}

export interface TemplateNarrative {
  id: string;
  reportId: string;
  /** ADR-0005 — narrative component code the template slot references. */
  narrativeCode?: string | null;
  /** ADR-0005 — component version the body came from (latest installed; browse context has no engagement pin). */
  narrativeVersion?: number | null;
  bodyHtml: string;
  /** JSON string — array of placeholder descriptors. Helper `parsePlaceholders` provided. */
  placeholders: string;
}

export interface NarrativePlaceholder {
  token: string;
  label: string;
  required?: boolean;
  source?: string;
  format?: string;
}

export function parsePlaceholders(n: TemplateNarrative): NarrativePlaceholder[] {
  try { return JSON.parse(n.placeholders || '[]'); } catch { return []; }
}

// Tree node shapes ---------------------------------------------------------

export interface TemplateScheduleNode {
  schedule: TemplateSchedule;
  /**
   * Top-level classes under the schedule (after fix 022 + 023, classes
   * link directly to the schedule — there's no longer a Sections layer).
   * Children nest via class.children. Use section_label on the class to
   * group visually inside a multi-section Note.
   */
  classes: ScheduleClass[];
}

export interface TemplateReportNode {
  report: TemplateReport;
  /** Empty for FS reports (which carry their structure in report.layout); populated for NOTES_BUNDLE. */
  schedules: TemplateScheduleNode[];
  /** Set only for reports with reportType = 'NARRATIVE'. */
  narrative?: TemplateNarrative | null;
}

export interface FsTemplateHierarchy {
  template: FsTemplate;
  reports: TemplateReportNode[];
}

// ============================================================================
// LIST QUERY
// ============================================================================

export interface FsTemplateSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  code?: string;
  name?: string;
  entityType?: EntityType;
  jurisdictionCode?: string;
  statutoryBasis?: string;
  isSystem?: boolean;
  statusCode?: string;
  includeInactive?: boolean;
  /** ADR-0005 — also list installed narrative components (Template Browser only; template pickers must omit). */
  includeNarratives?: boolean;
}
